/**
 * templatePackage.ts — read a downloaded template `.pptx` and build the single
 * slide to insert from it.
 *
 * Ordering: the template's slide is filled with the user's content BEFORE it
 * reaches the deck, not after. Office.js cannot set a slide's OOXML, so
 * patching an already-inserted slide would mean insert → rebuild → insert the
 * fixed copy → delete the first, with the raw template visible in between. By
 * fitting the content into the package first, the slide lands right the first
 * time.
 *
 * That is also why `/slides/transform-template` is given the template's OWN
 * slide XML rather than an inserted slide's: it only parses the XML to derive
 * slot capacities, and this way the mapping plan is built against exactly the
 * XML being patched, so plan keys and shapes cannot drift apart.
 *
 * A template `.pptx` can hold several slides; a catalog id names which one
 * ("transform-layouts/3_Column.pptx#1" — 1-based, per the catalog).
 */

import {
  DeckStructureError,
  listSlidePartPaths,
  readPartText,
  readSlideDimensions,
  unzipDeck,
  type PackageFiles,
} from "./pptxDeckParts";
import { buildSingleSlidePackage } from "./slideTransformWriteback";
import type { SlideDimensions } from "./slideTransformApi";

/** Slide number to use when a candidate carries none — the first slide. */
const DEFAULT_TEMPLATE_SLIDE_NUMBER = 1;

export interface TemplateSlide {
  /** The unzipped template package, reused when building the slide to insert. */
  readonly files: PackageFiles;
  /** Part path of the chosen slide, e.g. `ppt/slides/slide1.xml`. */
  readonly partPath: string;
  /** 0-based position of that slide in the template's presentation order. */
  readonly position: number;
  /** The chosen slide's whole XML document. */
  readonly slideXml: string;
  /** The template's own slide size, which the fitter needs for slot capacities. */
  readonly slideDimensions: SlideDimensions;
}

/**
 * Read the slide named by `slideNumber` (1-based) out of a template package.
 *
 * @throws {DeckStructureError} when the package has no slides, the requested
 * slide is out of range, or its part is unreadable.
 */
export function readTemplateSlide(
  templateBytes: ArrayBuffer,
  slideNumber: number | null | undefined,
): TemplateSlide {
  let files: PackageFiles;
  try {
    files = unzipDeck(templateBytes);
  } catch {
    // A truncated or non-zip body (an HTML error page served as 200, say)
    // fails here rather than as an opaque Office.js error much later.
    throw new DeckStructureError("The template file could not be read as a PowerPoint package.");
  }

  const slidePartPaths = listSlidePartPaths(files);
  if (slidePartPaths.length === 0) {
    throw new DeckStructureError("The template file contains no slides.");
  }

  // The catalog's index is 1-based; anything outside the package falls back to
  // the first slide, which is a usable layout rather than a hard failure.
  const requested = slideNumber ?? DEFAULT_TEMPLATE_SLIDE_NUMBER;
  const position = requested >= 1 && requested <= slidePartPaths.length ? requested - 1 : 0;
  const partPath = slidePartPaths[position]!;

  const slideXml = readPartText(files, partPath);
  if (!slideXml) {
    throw new DeckStructureError(`The template's slide ${position + 1} could not be read.`);
  }

  return {
    files,
    partPath,
    position,
    slideXml,
    slideDimensions: readSlideDimensions(files),
  };
}

/**
 * Package `template`'s chosen slide, carrying `filledSlideXml`, as a
 * single-slide base64 `.pptx` ready for `insertSlidesFromBase64`.
 *
 * Every other part of the template zip is kept intact, so the slide arrives
 * with the layout, master, theme and media relationships its design depends on.
 *
 * @throws {DeckStructureError} when the template package is missing a required part.
 */
export function buildTemplateSlidePackage(template: TemplateSlide, filledSlideXml: string): string {
  return buildSingleSlidePackage(
    template.files,
    template.partPath,
    template.position,
    filledSlideXml,
  ).base64;
}
